import { startOfWeek, endOfWeek } from 'date-fns';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Filters for the Sync Jobs dashboard. Replaces the Blazor
 * `Blazored.SessionStorage` value persisted under `summary-date-filters`.
 *
 * The React key is namespaced (`arp:sync-jobs:filters:v1`) to avoid stale
 * reads while the Blazor app and React app coexist.
 */

export interface SyncJobsFiltersState {
  startDate: string; // ISO date (yyyy-MM-dd)
  endDate: string;
  search: string;
  page: number;
  pageSize: number;
  sortCol: string;
  sortDir: 'asc' | 'desc' | '';
  setDateRange: (startDate: Date, endDate: Date) => void;
  setSearch: (value: string) => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setSort: (sortCol: string, sortDir: 'asc' | 'desc' | '') => void;
  reset: () => void;
}

function toIso(value: Date): string {
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, '0');
  const d = String(value.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function defaultRange(): { startDate: string; endDate: string } {
  const now = new Date();
  // Blazor used Monday → Sunday of the current week.
  return {
    startDate: toIso(startOfWeek(now, { weekStartsOn: 1 })),
    endDate: toIso(endOfWeek(now, { weekStartsOn: 1 })),
  };
}

const initial = defaultRange();

export const useSyncJobsFiltersStore = create<SyncJobsFiltersState>()(
  persist(
    (set) => ({
      startDate: initial.startDate,
      endDate: initial.endDate,
      search: '',
      page: 1,
      pageSize: 10,
      sortCol: '',
      sortDir: '',
      setDateRange: (startDate, endDate) =>
        set({ startDate: toIso(startDate), endDate: toIso(endDate), page: 1 }),
      setSearch: (value) => set({ search: value, page: 1 }),
      setPage: (page) => set({ page }),
      setPageSize: (pageSize) => set({ pageSize, page: 1 }),
      setSort: (sortCol, sortDir) => set({ sortCol, sortDir, page: 1 }),
      reset: () => {
        const range = defaultRange();
        set({
          startDate: range.startDate,
          endDate: range.endDate,
          search: '',
          page: 1,
          sortCol: '',
          sortDir: '',
        });
      },
    }),
    {
      name: 'arp:sync-jobs:filters:v1',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        startDate: state.startDate,
        endDate: state.endDate,
        pageSize: state.pageSize,
      }),
    },
  ),
);

export function parseIsoDate(iso: string | null | undefined): Date {
  // yyyy-MM-dd interpreted as local date (avoids the UTC midnight shift of
  // `new Date(iso)`). Falls back to today when the value is missing or
  // malformed so a corrupted sessionStorage entry can never crash the
  // downstream `query.startDate.toISOString()` call.
  if (typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    return new Date();
  }
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? new Date() : date;
}
